import { useFeatureFlag } from '@user-portal/experimentation';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectCountry, selectCurrentUser } from 'store/user/selectors';

import {
    SPECIAL_OFFERS,
    SUBSCRIPTION_ACTIVE,
    SUBSCRIPTION_CANCELLED,
    SUBSCRIPTION_CHARGEBACK,
    SUBSCRIPTION_PAUSED,
    SUBSCRIPTION_PENDING,
    SUBSCRIPTION_REDEMPTION,
} from 'constants/payments';

import { cancelFlow_subscriptionClick } from 'services/analytics/trackers/cancelFlow';

import { normalizeDateByLocale } from 'helpers/normalizeDateByLocale';
import { getPriceFromCents } from 'helpers/prices';
import { replaceUnderscoreToDash } from 'helpers/utils';

import { getSubscriptionIcon, getSubscriptionName, getUpcomingProduct, isMainSubscription } from '../helpers';
import { ISubscriptionCard } from '../types';

const TRIAL_NAME = 'subscription.type.trial';
const SUBSCRIPTION_NAME = 'subscription.type.subscription';
const SINGLE_BILLING_CHARGE = 1;

const useSubscriptionCard = ({ onCancelSubscription, subscription }: ISubscriptionCard) => {
    const {
        fee,
        state,
        reminder,
        discounts,
        product,
        is_trial: isTrial,
        is_upgrade: isUpgrade,
        expired_date: expiredDate,
        cancelled_at: cancelledAt,
        product_code: productCode,
        period: subscriptionPeriod,
        trial_period: subscriptionTrialPeriod,
        internal_cancel_at: internalCancelAt,
        payment_method: paymentMethod,
        billing_cycles: billingCycles,
        is_promo_code_used: isPromoCodeUsed,
        id: subscriptionId,
        product_type: productCodeType,
        product_description: productDescription,
    } = subscription;
    const { t } = useTranslation();

    const fullPrice = product?.price || 0;
    const currency = product?.currency || '';
    const trialPrice = product?.trial_price || 0;

    const isSubscriptionPaused = Boolean(state === SUBSCRIPTION_PAUSED && !discounts.free_month);
    const isFreeMonthActive = Boolean(state === SUBSCRIPTION_PAUSED && discounts.free_month);
    const isCancelledSubscription =
        state === SUBSCRIPTION_CANCELLED || Boolean(cancelledAt) || Boolean(internalCancelAt);
    const isSubscriptionActive = state === SUBSCRIPTION_ACTIVE;
    const isSubscriptionRedemption = state === SUBSCRIPTION_REDEMPTION;
    const isExpiredSubscription =
        (isCancelledSubscription && !isSubscriptionActive) || state === SUBSCRIPTION_CHARGEBACK;
    const isExpiresSubscription = isCancelledSubscription && isSubscriptionActive;
    const isPendingSubscription = state === SUBSCRIPTION_PENDING;
    const canPauseSubscription = !discounts[SPECIAL_OFFERS.PAUSE_SUBSCRIPTION] && isMainSubscription(productCodeType);

    const country = useSelector(selectCountry);
    const { timezone } = useSelector(selectCurrentUser);

    const expirationDate = normalizeDateByLocale({ date: expiredDate, country, timezone });
    const cancelledAtDate = normalizeDateByLocale({ date: cancelledAt, country, timezone });

    const tabIcon = getSubscriptionIcon();

    const { value: isDynamicProductDescriptionsEnabled } = useFeatureFlag<boolean>({
        type: 'boolean',
        flagKey: 'dynamic_product_descriptions',
        defaultValue: false,
    });

    const heading = replaceUnderscoreToDash(
        getSubscriptionName(
            productCode,
            isUpgrade,
            isDynamicProductDescriptionsEnabled ? productDescription : undefined
        )
    );
    const price = isTrial ? trialPrice : fullPrice;

    const feeData = fee?.fee_enabled
        ? {
              fee_amount: `${getPriceFromCents(fee.fee_amount, currency)} ${currency}`,
              fee_total_price: `${getPriceFromCents(fee.fee_total_price, currency)} ${currency}`,
              fee_expired_at: normalizeDateByLocale({ date: fee.fee_expired_at, country, timezone }),
              fee_period: fee.fee_period ? fee.fee_period * subscriptionPeriod : 365,
          }
        : null;

    const handleCancelSubscription = () => {
        cancelFlow_subscriptionClick({
            status: state,
            action: 'cancel',
            reminder: !!reminder,
            free_month: isFreeMonthActive,
            subscription_id: subscription?.external_id || '',
        });

        onCancelSubscription(subscription);
    };

    const getExpirationDate = () => {
        if (isExpiresSubscription) {
            return replaceUnderscoreToDash(expirationDate);
        }

        return replaceUnderscoreToDash(cancelledAt ? cancelledAtDate : expirationDate);
    };

    return {
        subscriptionId,
        feeData,
        price: `${price} ${currency}`,
        state,
        tabIcon,
        heading,
        upcomingProduct: getUpcomingProduct({ subscription, country, timezone }),
        isSubscriptionRedemption,
        isExpiresSubscription,
        isExpiredSubscription,
        isPendingSubscription,
        isSubscriptionPaused,
        isFreeMonthActive,
        cancelledAtDate,
        isMainSubscription: isMainSubscription(productCodeType),
        handleCancelSubscription,
        subscriptionType: t(isTrial ? TRIAL_NAME : SUBSCRIPTION_NAME),
        expirationDate: getExpirationDate(),
        subscriptionPeriod: isTrial ? subscriptionTrialPeriod : subscriptionPeriod,
        isUpgrade,
        paymentMethod,
        isOneTimeProduct: billingCycles === SINGLE_BILLING_CHARGE && !isTrial,
        isPromoCodeUsed,
        canPauseSubscription,
    };
};

export default useSubscriptionCard;
